import { RoutePaths } from "@/core/constants/RoutePaths";
import { captureWhenReady } from "@/shared/lib/analytics";
import { BUILD_APP_VERSION } from "@/features/app-version/utils/appVersion";
import { runWhenIdle } from "@/shared/utils/runWhenIdle";

/**
 * Tempo de carregamento do painel medido no navegador do cliente (SA-1469).
 *
 * Dois eventos, cada um uma vez por documento:
 * - `panel_page_load`: TTFB e carga do documento, na rota em que a pessoa
 *   entrou. LCP, INP, CLS e FCP vêm à parte, no `$web_vitals` do PostHog.
 * - `panel_chat_list_loaded`: do começo (carga da página ou envio do login)
 *   até a primeira lista de conversas pintada.
 *
 * Consultas e linha de base em `docs/perf/acompanhamento.md`.
 */

export const PAGE_LOAD_EVENT = "panel_page_load";
export const CHAT_LIST_LOADED_EVENT = "panel_chat_list_loaded";

/** Acima disso a medida é aba esquecida aberta, não carregamento. */
export const MAX_REPORTED_DURATION_MS = 120_000;

export type ChatListStart = "page_load" | "login";
export type LoginMethod = "password" | "google";
export type ChatListOutcome = "ok" | "empty" | "error";

type State = {
  landingPath: string;
  loginMark: { at: number; method: LoginMethod } | null;
  /** A aba ficou escondida desde a carga (vale para `panel_page_load`). */
  wasHidden: boolean;
  /** A aba ficou escondida desde que o relógio da lista começou. */
  listWasHidden: boolean;
  pageLoadReported: boolean;
  chatListDone: boolean;
};

let state: State | null = null;

const isTabHidden = () => document.visibilityState === "hidden";

function getState(): State {
  if (state) return state;
  state = {
    landingPath: window.location.pathname,
    loginMark: null,
    wasHidden: isTabHidden(),
    listWasHidden: isTabHidden(),
    pageLoadReported: false,
    chatListDone: false,
  };
  // Aba em segundo plano congela timers e pintura: a medida vira lixo, mas
  // continua indo, marcada, para não sumir sem explicação.
  document.addEventListener("visibilitychange", () => {
    if (!isTabHidden() || !state) return;
    state.wasHidden = true;
    state.listWasHidden = true;
  });
  return state;
}

/**
 * Chame o quanto antes (no `main.tsx`): guarda a rota de entrada e se a aba
 * já nasceu escondida, e agenda o `panel_page_load` para o ocioso depois do
 * `load`.
 */
export function startPanelLoadTiming(): void {
  getState();
  if (document.readyState === "complete") {
    runWhenIdle(reportPageLoad);
    return;
  }
  window.addEventListener("load", () => runWhenIdle(reportPageLoad), {
    once: true,
  });
}

/**
 * Troca ids por `:id` (o `documentId` do Strapi tem 24 caracteres, e há ids
 * numéricos) para agrupar por rota, não por conversa.
 */
export function routePattern(pathname: string): string {
  const normalized = pathname
    .split("/")
    .map((segment) =>
      /^\d+$/.test(segment) || /^[a-z0-9]{20,}$/.test(segment) ? ":id" : segment
    )
    .join("/");
  return normalized === "" ? "/" : normalized;
}

function navigationEntry(): PerformanceNavigationTiming | undefined {
  const [entry] = performance.getEntriesByType?.("navigation") ?? [];
  return entry as PerformanceNavigationTiming | undefined;
}

/**
 * Em página pré-renderizada, quando a pessoa de fato abriu a aba (0 nas
 * demais): a espera antes disso não é carregamento.
 */
function activationStart(): number {
  const entry = navigationEntry() as
    | (PerformanceNavigationTiming & { activationStart?: number })
    | undefined;
  return entry?.activationStart ?? 0;
}

function connectionType(): string | undefined {
  const connection = (
    navigator as Navigator & { connection?: { effectiveType?: string } }
  ).connection;
  return connection?.effectiveType;
}

function commonProperties(current: State) {
  return {
    app_version: BUILD_APP_VERSION,
    landing_route: routePattern(current.landingPath),
    navigation_type: navigationEntry()?.type,
    connection_type: connectionType(),
  };
}

/**
 * TTFB e carga do documento. Chame depois do `load` (o `loadEventEnd` só
 * existe depois dele).
 */
export function reportPageLoad(): void {
  const current = getState();
  if (current.pageLoadReported) return;
  const entry = navigationEntry();
  if (!entry) return;
  current.pageLoadReported = true;

  const activatedAt = activationStart();
  const since = (value: number) => Math.round(Math.max(0, value - activatedAt));

  captureWhenReady(PAGE_LOAD_EVENT, {
    ...commonProperties(current),
    was_hidden: current.wasHidden,
    ttfb_ms: since(entry.responseStart),
    dom_content_loaded_ms: since(entry.domContentLoadedEventEnd),
    load_ms: since(entry.loadEventEnd),
    transfer_kb: Math.round((entry.transferSize ?? 0) / 1024),
  });
}

/**
 * O login (senha ou Google) deu certo e o próximo passo é a lista: o relógio
 * dela recomeça em `submittedAt` (`performance.now()` de quando o login foi
 * enviado), mesmo que a lista já tenha sido medida ou abandonada antes neste
 * documento (sessão expirada que caiu no login, sair e entrar de novo). Quem
 * vai para o onboarding não chama.
 */
export function markLoginSubmitted(
  method: LoginMethod,
  submittedAt: number
): void {
  const current = getState();
  current.loginMark = { at: submittedAt, method };
  current.chatListDone = false;
  current.listWasHidden = isTabHidden();
}

export function isChatsPath(pathname: string): boolean {
  return (
    pathname === RoutePaths.CHATS || pathname.startsWith(`${RoutePaths.CHATS}/`)
  );
}

/**
 * Rotas pelas quais a carga passa a caminho da lista sem virar navegação
 * interna: a raiz (login, ou redirecionamento de quem já está logado), a volta
 * do Google e a própria lista.
 */
function isOnTheWayToChats(pathname: string): boolean {
  return (
    pathname === RoutePaths.LOGIN ||
    pathname === RoutePaths.GOOGLE_CALLBACK ||
    isChatsPath(pathname)
  );
}

/**
 * Cada troca de rota passa por aqui (`PanelLoadRouteWatcher`). Qualquer
 * desvio antes da lista (cadastro, onboarding, `/ativar-plano`, outra tela)
 * encerra a medida: o tempo até a lista deixaria de ser carregamento. Um login
 * depois disso reabre o relógio.
 */
export function notePanelRoute(pathname: string): void {
  if (!isOnTheWayToChats(pathname)) skipChatListTiming();
}

/**
 * A primeira lista de conversas acabou de ser montada. Mede no quadro
 * seguinte, depois da pintura. Vale uma vez por carga ou por login, e só se a
 * pessoa veio direto para a lista: com desvio no caminho, `notePanelRoute` já
 * encerrou a medida.
 */
export function reportChatListLoaded({
  outcome,
  chatCount,
  tab,
}: {
  outcome: ChatListOutcome;
  chatCount: number;
  tab: string;
}): void {
  const current = getState();
  if (current.chatListDone) return;
  current.chatListDone = true;

  const login = current.loginMark;
  const start: ChatListStart = login ? "login" : "page_load";
  const startedAt = login ? login.at : activationStart();

  const send = () => {
    const duration = Math.round(performance.now() - startedAt);
    if (duration > MAX_REPORTED_DURATION_MS) return;

    captureWhenReady(CHAT_LIST_LOADED_EVENT, {
      ...commonProperties(current),
      was_hidden: current.listWasHidden,
      start,
      login_method: login?.method,
      duration_ms: duration,
      outcome,
      chat_count: chatCount,
      tab,
    });
  };

  // Aba escondida não pinta nem roda `requestAnimationFrame`: esperar o
  // quadro seria esperar a pessoa voltar. Manda já, marcada como escondida.
  if (isTabHidden()) {
    current.listWasHidden = true;
    send();
    return;
  }
  requestAnimationFrame(() => window.setTimeout(send, 0));
}

/**
 * Desiste da medida da lista até o próximo login: chegar nela depois disso é
 * navegação interna, ou a tela não mostra lista (setor sem WhatsApp, conversa
 * aberta no celular).
 */
export function skipChatListTiming(): void {
  getState().chatListDone = true;
}

/** Só para testes: o estado vive no módulo, um por documento. */
export function resetPanelLoadTimingForTests(): void {
  state = null;
}
